import type { Database } from '@/types/supabase';
import { handleError, logError, ErrorType, AppError } from '@/utils/error/errorHandler';
import { cacheManager } from '@/utils/cache/cacheManager';
import { 
  CacheEntityType, 
  CACHE_TTL, 
  CACHE_KEY_PATTERNS 
} from '@/utils/cache/cacheConfig';
import { 
  invalidateEntityCache, 
  invalidateEntityById, 
  invalidateRelatedCaches 
} from '@/utils/cache/cacheInvalidation';
import { validateUUID, validate } from '@/utils/validationWrapper';
import { deathmatchSchema, deathmatchRoundSchema } from '@/validation/schemas';
import { fetchWithAuth } from '@/lib/apiUtils';

type Deathmatch = Database['public']['Tables']['deathmatches']['Row'];
type InsertDeathmatch = Database['public']['Tables']['deathmatches']['Insert'];
type UpdateDeathmatch = Database['public']['Tables']['deathmatches']['Update'];
type DeathmatchRound = Database['public']['Tables']['deathmatch_rounds']['Row'];
type InsertDeathmatchRound = Database['public']['Tables']['deathmatch_rounds']['Insert'];
type DeathmatchDetails = Database['public']['Views']['deathmatch_details']['Row'];
type DeathmatchBracket = Database['public']['Tables']['deathmatch_brackets']['Row'];
type InsertDeathmatchBracket = Database['public']['Tables']['deathmatch_brackets']['Insert'];
type UpdateDeathmatchBracket = Database['public']['Tables']['deathmatch_brackets']['Update'];

// Cache TTL constants
// const CACHE_TTL = {
//   SHORT: 1 * 60 * 1000, // 1 minute
//   MEDIUM: 5 * 60 * 1000, // 5 minutes
//   LONG: 30 * 60 * 1000, // 30 minutes
// };

// Cache key prefixes - using the old format for backward compatibility
// These will be gradually replaced with the new CACHE_KEY_PATTERNS
const CACHE_KEYS = {
  ALL_DEATHMATCHES: 'deathmatches:all',
  DEATHMATCH_BY_ID: 'deathmatch:id:',
  DEATHMATCH_ROUNDS: 'deathmatch:rounds:',
  DEATHMATCH_DETAILS: 'deathmatch:details:',
  ALL_DEATHMATCH_DETAILS: 'deathmatches:all_details',
};

/**
 * Invalidates all deathmatch-related cache entries
 * @deprecated Use invalidateEntityCache(CacheEntityType.DEATHMATCH) instead
 */
export function invalidateDeathmatchCache(): void {
  invalidateEntityCache(CacheEntityType.DEATHMATCH);
}

/**
 * Invalidates cache entries for a specific deathmatch
 * @param deathmatchId - The deathmatch's UUID
 * @deprecated Use invalidateEntityById(CacheEntityType.DEATHMATCH, deathmatchId) instead
 */
export function invalidateDeathmatchCacheById(deathmatchId: string): void {
  invalidateEntityById(CacheEntityType.DEATHMATCH, deathmatchId);
}

/**
 * Gets all deathmatches from the API
 * @param includeCompleted - Whether to include completed deathmatches (default: true)
 * @returns Array of deathmatches
 * @throws AppError if the operation fails
 */
export async function getAllDeathmatches(includeCompleted: boolean = true): Promise<Deathmatch[]> {
  // Cache key based on parameters
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.DEATHMATCH)}:completed=${includeCompleted}`;
  const cachedDeathmatches = cacheManager.get<Deathmatch[]>(cacheKey);
  if (cachedDeathmatches) {
    console.log(`Returning cached deathmatches (completed=${includeCompleted})`);
    return cachedDeathmatches; 
  }
  
  try {
    console.log(`Fetching deathmatches from API (completed=${includeCompleted})...`);
    const apiUrl = `/api/deathmatches?includeCompleted=${includeCompleted}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      const errorBody = await response.text();
      throw new AppError({
        message: `API error fetching deathmatches: ${response.status} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { endpoint: apiUrl, responseBody: errorBody }
      });
    }

    const data = await response.json();
    
    // Assuming API returns { deathmatches: Deathmatch[] }
    if (!data || !Array.isArray(data.deathmatches)) {
      throw new AppError({
        message: 'Invalid response structure from deathmatches API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const deathmatches: Deathmatch[] = data.deathmatches;
    
    console.log(`Caching ${deathmatches.length} deathmatches (completed=${includeCompleted})`);
    cacheManager.set(cacheKey, deathmatches, CACHE_TTL.SHORT);
    
    return deathmatches;

  } catch (error) {
    // Handle API errors or unexpected issues
    const appError = handleError(error, 'getAllDeathmatches', { includeCompleted });
    logError(appError, 'getAllDeathmatches');
    
    // For API errors classified as NOT_FOUND (e.g., if API returned 404), return empty array
    // Note: A 404 on a list endpoint is unlikely but handled defensively.
    if (appError.type === ErrorType.NOT_FOUND || (appError.type === ErrorType.NETWORK && appError.statusCode === 404)) {
      console.warn('Deathmatches endpoint returned 404, returning empty array.');
      return [];
    }
    
    throw appError; // Re-throw other errors
  }
}

/**
 * Creates a new deathmatch via the backend API
 * @param poemIds - Array of poem IDs
 * @returns The created deathmatch object
 * @throws AppError if the operation fails
 */
export async function createDeathmatch(poemIds: string[]): Promise<Deathmatch> {
  try {
    // Validate input poemIds array
    if (!Array.isArray(poemIds) || poemIds.length === 0) {
        throw new AppError('poemIds must be a non-empty array', ErrorType.VALIDATION);
    }
    // Optional: Add UUID validation for each ID in the array here if desired
    // const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    // poemIds.forEach(id => {
    //   if (typeof id !== 'string' || !uuidRegex.test(id)) {
    //     throw new AppError(`Invalid poem ID format: ${id}`, ErrorType.VALIDATION);
    //   }
    // });

    console.log(`Creating deathmatch via API with ${poemIds.length} poems...`);

    const response = await fetchWithAuth('/api/deathmatches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send the poemIds array in the expected format
      body: JSON.stringify({ poemIds: poemIds }),
    });

    console.log(`[deathmatchService] createDeathmatch - Response Status: ${response.status}`);

    if (!response.ok) {
      // Log the body specifically on error before throwing
      const errorBody = await response.text(); 
      console.error(`[deathmatchService] createDeathmatch - Error Response Body:`, errorBody);
      throw new AppError({
        message: `API error creating deathmatch: ${response.status} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { endpoint: '/api/deathmatches', responseBody: errorBody }
      });
    }

    // Attempt to parse JSON only if response.ok
    const data = await response.json();

    // Assuming API returns the created deathmatch object directly (as modified in deathmatchAPI.ts)
    // Adjust this if the backend API wraps it, e.g., { deathmatch: ... }
    if (!data || typeof data !== 'object' || data === null || !data.id) { // Check for presence of id
       throw new AppError({
        message: 'Invalid response structure from create deathmatch API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const createdDeathmatch: Deathmatch = data; // Assign directly

    // Invalidate cache using the new utilities
    // Invalidate the list cache, specific ID invalidation might not be needed if list includes all data
    invalidateEntityCache(CacheEntityType.DEATHMATCH);

    return createdDeathmatch;

  } catch (error) {
    const appError = handleError(error, 'createDeathmatch', { poemIdsCount: poemIds?.length });
    logError(appError, 'createDeathmatch');
    throw appError;
  }
}

/**
 * Deletes a deathmatch via the backend API.
 * The backend API handles the transaction to delete associated rounds.
 * 
 * @deprecated As noted in the plan, deletion is not a standard user flow.
 * 
 * @param deathmatchId - The deathmatch's UUID
 * @returns True if the deathmatch was deleted (or didn't exist), false otherwise
 * @throws AppError if the operation fails
 */
export async function deleteDeathmatch(deathmatchId: string): Promise<boolean> {
  try {
    // Validate UUID format
    validateUUID(deathmatchId, 'deathmatchId');
    
    console.log(`Deleting deathmatch ${deathmatchId} via API...`);
    // Remove withTransaction wrapper
    // const result = await withTransaction(async (client) => { ... });

    const apiUrl = `/api/deathmatches/${encodeURIComponent(deathmatchId)}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'DELETE',
    });

    // Treat 404 as success for delete
    if (!response.ok && response.status !== 404) {
      const errorBody = await response.text();
      throw new AppError({
        message: `API error deleting deathmatch ${deathmatchId}: ${response.status} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { endpoint: apiUrl, responseBody: errorBody }
      });
    }

    // Invalidate cache using the new utilities
    console.log(`Invalidating cache for deleted deathmatch ${deathmatchId}`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, deathmatchId);
    
    return true; // Return true if API call succeeded or returned 404

  } catch (error) {
    const appError = handleError(error, 'deleteDeathmatch', { deathmatchId });
    logError(appError, 'deleteDeathmatch');
    throw appError;
  }
}

/**
 * Adds a round to an existing deathmatch via the backend API.
 * The API is expected to handle internal logic like determining round type and positions.
 * @param round - The round data to insert (minimal required fields)
 * @returns The created deathmatch round object
 * @throws AppError if the operation fails
 */
export async function addRoundToDeathmatch(
  // Simplify input type: only need essential info for API call
  round: Pick<InsertDeathmatchRound, 'deathmatch_id' | 'round_number' | 'poem1_id' | 'poem2_id' | 'winner_id'>
): Promise<DeathmatchRound> {
  try {
    // Validate incoming basic round data (IDs, round number)
    validateUUID(round.deathmatch_id, 'deathmatch_id');
    validateUUID(round.poem1_id, 'poem1_id');
    validateUUID(round.poem2_id, 'poem2_id');
    if (round.winner_id) validateUUID(round.winner_id, 'winner_id');
    if (typeof round.round_number !== 'number' || round.round_number < 1) {
        throw new AppError('Invalid round number', ErrorType.VALIDATION);
    }

    // Removed logic for finding positions/determining type - API responsibility

    console.log(`Adding round ${round.round_number} to deathmatch ${round.deathmatch_id} via API...`);

    const apiUrl = `/api/deathmatches/${encodeURIComponent(round.deathmatch_id)}/rounds`;
    const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        // Send only the necessary data for the API
        body: JSON.stringify(round),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new AppError({
        message: `API error adding round: ${response.status} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { endpoint: apiUrl, responseBody: errorBody, inputRound: round }
      });
    }
    
    const data = await response.json();

    // Assuming API returns { round: DeathmatchRound }
    if (!data || typeof data.round !== 'object' || data.round === null) {
       throw new AppError({
        message: 'Invalid response structure from add round API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const createdRound: DeathmatchRound = data.round;
    
    // Invalidate relevant caches
    console.log(`Invalidating cache after adding round to ${round.deathmatch_id}`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, round.deathmatch_id, {
      poem1_id: round.poem1_id,
      poem2_id: round.poem2_id,
      winner_id: round.winner_id
    });
    
    return createdRound;

  } catch (error) {
    const appError = handleError(error, 'addRoundToDeathmatch', { round });
    logError(appError, 'addRoundToDeathmatch');
    throw appError;
  }
}

/**
 * Retrieves all rounds for a specific deathmatch from the API
 * @param deathmatchId - The deathmatch's UUID
 * @returns Array of deathmatch round objects
 * @throws AppError if the operation fails
 */
export async function getAllRoundsForDeathmatch(deathmatchId: string): Promise<DeathmatchRound[]> {
  try {
    validateUUID(deathmatchId, 'deathmatchId');
    
    const cacheKey = `${CACHE_KEY_PATTERNS.BY_RELATION(
      CacheEntityType.DEATHMATCH, CacheEntityType.DEATHMATCH, deathmatchId
    )}:rounds`; // Maintain cache key structure
    
    const cachedRounds = cacheManager.get<DeathmatchRound[]>(cacheKey);
    if (cachedRounds) {
      console.log(`Returning cached rounds for deathmatch ${deathmatchId}`);
      return cachedRounds;
    }
    
    console.log(`Fetching rounds for deathmatch ${deathmatchId} from API...`);
    const apiUrl = `/api/deathmatches/${encodeURIComponent(deathmatchId)}/rounds`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
       // Handle 404 specifically - means deathmatch exists but has no rounds (or deathmatch not found)
       if (response.status === 404) {
         console.warn(`API returned 404 for rounds for deathmatch ${deathmatchId}. Returning empty array.`);
         // Cache the empty result to avoid refetching
         cacheManager.set(cacheKey, [], CACHE_TTL.SHORT);
         return [];
       }
       const errorBody = await response.text();
       throw new AppError({
         message: `API error fetching rounds for deathmatch ${deathmatchId}: ${response.status} ${response.statusText}`,
         type: ErrorType.NETWORK,
         statusCode: response.status,
         metadata: { endpoint: apiUrl, responseBody: errorBody }
       });
    }

    const data = await response.json();

    // Assuming API returns { rounds: DeathmatchRound[] }
     if (!data || !Array.isArray(data.rounds)) {
       throw new AppError({
         message: 'Invalid response structure from rounds API',
         type: ErrorType.UNEXPECTED,
         statusCode: 500,
         metadata: { responseData: data }
       });
     }

    const rounds: DeathmatchRound[] = data.rounds;
    
    console.log(`Caching ${rounds.length} rounds for deathmatch ${deathmatchId}`);
    cacheManager.set(cacheKey, rounds, CACHE_TTL.SHORT);
    
    return rounds;

  } catch (error) {
    const appError = handleError(error, 'getAllRoundsForDeathmatch', { deathmatchId });
    logError(appError, 'getAllRoundsForDeathmatch');
    
    // Let error handler decide if 404 should be suppressed or thrown
    // Based on current logic, NOT_FOUND (potentially from API 404) returns []
    if (appError.type === ErrorType.NOT_FOUND) {
       console.warn(`Error handled as NOT_FOUND for rounds for deathmatch ${deathmatchId}. Returning empty array.`);
      return [];
    }
    
    throw appError;
  }
}

/**
 * Updates the winner of a deathmatch round via the backend API.
 * @param roundId - The round's UUID
 * @param winnerId - The winning poem's UUID
 * @returns The updated deathmatch round object
 * @throws AppError if the round is not found or the operation fails
 */
export async function updateRoundWinner(roundId: string, winnerId: string): Promise<DeathmatchRound> {
  try {
    // Validate UUID formats
    validateUUID(roundId, 'roundId');
    validateUUID(winnerId, 'winnerId');
    
    console.log(`Updating winner for round ${roundId} to ${winnerId} via API...`);
    // Note: We need the deathmatchId for the API endpoint, 
    // but it's not passed to this function. This implies the API 
    // might need restructuring OR this service function needs refactoring 
    // to first fetch the round to get the deathmatchId, which is inefficient.
    // **Assumption:** The API takes PUT /api/deathmatch-rounds/:roundId
    // If not, this needs revision based on actual API design.

    const apiUrl = `/api/deathmatch-rounds/${encodeURIComponent(roundId)}`; // Assumed API structure
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ winner_id: winnerId }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
      // Handle 404 specifically
      if (statusCode === 404) {
        throw new AppError({
          message: `Round with ID ${roundId} not found via API`,
          type: ErrorType.NOT_FOUND,
          statusCode: 404,
          metadata: { endpoint: apiUrl, roundId, winnerId }
        });
      }
      // Handle other errors
      throw new AppError({
        message: `API error updating round winner: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, roundId, winnerId }
      });
    }

    const data = await response.json();

    // Assuming API returns { round: DeathmatchRound }
    if (!data || typeof data.round !== 'object' || data.round === null) {
      throw new AppError({
        message: 'Invalid response structure from update round winner API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const updatedRound: DeathmatchRound = data.round;
    
    // Invalidate relevant caches using the deathmatch_id from the response
    console.log(`Invalidating cache after updating winner for round ${roundId} (deathmatch ${updatedRound.deathmatch_id})`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, updatedRound.deathmatch_id, {
      poem1_id: updatedRound.poem1_id,
      poem2_id: updatedRound.poem2_id,
      winner_id: winnerId
    });
    
    return updatedRound;

  } catch (error) {
    const appError = handleError(error, 'updateRoundWinner', { roundId, winnerId });
    logError(appError, 'updateRoundWinner');
    throw appError;
  }
}

/**
 * Marks a deathmatch as completed via the backend API.
 * @param deathmatchId - The deathmatch's UUID
 * @returns The updated deathmatch object
 * @throws AppError if the operation fails
 */
export async function completeDeathmatch(deathmatchId: string): Promise<Deathmatch> {
  try {
    // Validate UUID format
    validateUUID(deathmatchId, 'deathmatchId');
    
    console.log(`Completing deathmatch ${deathmatchId} via API...`);
    
    // Assuming API endpoint is PUT /api/deathmatches/:id 
    // and we send the completed status in the body.
    const apiUrl = `/api/deathmatches/${encodeURIComponent(deathmatchId)}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ completed: true }), // Send completion status
    });
    
    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
      if (statusCode === 404) {
         throw new AppError({
          message: `Deathmatch ${deathmatchId} not found via API for completion`,
          type: ErrorType.NOT_FOUND,
          statusCode: 404,
          metadata: { endpoint: apiUrl, deathmatchId }
        });
      }
      throw new AppError({
        message: `API error completing deathmatch ${deathmatchId}: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, deathmatchId }
      });
    }
    
    const data = await response.json();

    // Assuming API returns { deathmatch: Deathmatch }
    if (!data || typeof data.deathmatch !== 'object' || data.deathmatch === null || !data.deathmatch.completed) {
       throw new AppError({
        message: 'Invalid response structure from complete deathmatch API or not marked completed',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const completedDeathmatch: Deathmatch = data.deathmatch;
    
    // Invalidate cache using the new utilities
    console.log(`Invalidating cache for completed deathmatch ${deathmatchId}`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, deathmatchId);
    // Also invalidate details caches
    cacheManager.delete(`${CACHE_KEYS.DEATHMATCH_DETAILS}${deathmatchId}`);
    cacheManager.delete(CACHE_KEYS.ALL_DEATHMATCH_DETAILS);
    
    return completedDeathmatch;

  } catch (error) {
    const appError = handleError(error, 'completeDeathmatch', { deathmatchId });
    logError(appError, 'completeDeathmatch');
    throw appError;
  }
}

/**
 * Retrieves a deathmatch by ID with all rounds from the API.
 * Assumes the API endpoint returns the deathmatch and its associated rounds.
 * @param id - The deathmatch's UUID
 * @returns The deathmatch object with rounds
 * @throws AppError if the deathmatch is not found or the operation fails
 */
export async function getDeathmatchById(id: string): Promise<Deathmatch & { deathmatch_rounds: DeathmatchRound[] }> {
  try {
    validateUUID(id, 'id');
    
    const cacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.DEATHMATCH, id);
    const cachedDeathmatch = cacheManager.get<Deathmatch & { deathmatch_rounds: DeathmatchRound[] }>(cacheKey);
    if (cachedDeathmatch) {
      console.log(`Returning cached deathmatch by ID: ${id}`);
      return cachedDeathmatch;
    }
    
    console.log(`Fetching deathmatch by ID: ${id} from API...`);
    const apiUrl = `/api/deathmatches/${encodeURIComponent(id)}`; // Assumes API includes rounds
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
      if (statusCode === 404) {
        throw new AppError({
          message: `Deathmatch with ID ${id} not found via API`,
          type: ErrorType.NOT_FOUND,
          statusCode: 404,
          metadata: { endpoint: apiUrl, deathmatchId: id }
        });
      }
      throw new AppError({
        message: `API error fetching deathmatch ${id}: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, deathmatchId: id }
      });
    }

    const data = await response.json();

    // Assuming API returns { deathmatch: Deathmatch & { deathmatch_rounds: DeathmatchRound[] } }
    if (!data || typeof data.deathmatch !== 'object' || data.deathmatch === null || !Array.isArray(data.deathmatch.deathmatch_rounds)) {
      throw new AppError({
        message: `Invalid response structure from get deathmatch by ID API for ${id}`,
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }
    
    const deathmatchWithRounds: Deathmatch & { deathmatch_rounds: DeathmatchRound[] } = data.deathmatch;

    console.log(`Caching deathmatch by ID: ${id}`);
    cacheManager.set(cacheKey, deathmatchWithRounds, CACHE_TTL.SHORT);
    
    return deathmatchWithRounds;

  } catch (error) {
    const appError = handleError(error, 'getDeathmatchById', { deathmatchId: id });
    logError(appError, 'getDeathmatchById');
    throw appError;
  }
}

/**
 * Retrieves detailed information about a deathmatch from the API.
 * @param deathmatchId - The deathmatch's UUID
 * @returns Detailed deathmatch object with rounds and poems
 * @throws AppError if the deathmatch is not found or the operation fails
 */
export async function getDeathmatchDetails(deathmatchId: string): Promise<DeathmatchDetails> {
  try {
    validateUUID(deathmatchId, 'deathmatchId');
    
    // Use same cache key pattern, but might point to different data structure if details differ significantly
    const cacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.DEATHMATCH, deathmatchId) + ':details'; 
    const cachedDetails = cacheManager.get<DeathmatchDetails>(cacheKey);
    if (cachedDetails) {
      console.log(`Returning cached deathmatch details: ${deathmatchId}`);
      return cachedDetails;
    }
    
    console.log(`Fetching deathmatch details: ${deathmatchId} from API...`);
    const apiUrl = `/api/deathmatches/${encodeURIComponent(deathmatchId)}/details`; 
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
      if (statusCode === 404) {
         throw new AppError({
          message: `Deathmatch details with ID ${deathmatchId} not found via API`,
          type: ErrorType.NOT_FOUND,
          statusCode: 404,
          metadata: { endpoint: apiUrl, deathmatchId }
        });
      }
      throw new AppError({
        message: `API error fetching deathmatch details ${deathmatchId}: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, deathmatchId }
      });
    }

    const data = await response.json();

    // Assuming API returns { details: DeathmatchDetails }
    if (!data || typeof data.details !== 'object' || data.details === null) {
      throw new AppError({
        message: `Invalid response structure from get deathmatch details API for ${deathmatchId}`,
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const details: DeathmatchDetails = data.details;
    
    console.log(`Caching deathmatch details: ${deathmatchId}`);
    cacheManager.set(cacheKey, details, CACHE_TTL.SHORT);
    
    return details;

  } catch (error) {
    const appError = handleError(error, 'getDeathmatchDetails', { deathmatchId });
    logError(appError, 'getDeathmatchDetails');
    throw appError;
  }
}

/**
 * Retrieves all deathmatches with detailed information from the API.
 * @param includeCompleted - Whether to include completed deathmatches (default: true)
 * @param limit - Maximum number of deathmatches to return (default: 10)
 * @returns Array of detailed deathmatch objects
 * @throws AppError if the operation fails
 */
export async function getAllDeathmatchDetails(
  includeCompleted: boolean = true,
  limit: number = 10
): Promise<DeathmatchDetails[]> {
  const cacheKey = `${CacheEntityType.DEATHMATCH}s:all:details:${includeCompleted}:${limit}`; // Keep cache key
  const cachedDetails = cacheManager.get<DeathmatchDetails[]>(cacheKey);
  if (cachedDetails) {
    console.log(`Returning cached deathmatch details (completed=${includeCompleted}, limit=${limit})`);
    return cachedDetails;
  }
  
  try {
    console.log(`Fetching deathmatch details from API (completed=${includeCompleted}, limit=${limit})...`);
    const apiUrl = `/api/deathmatches/details?includeCompleted=${includeCompleted}&limit=${limit}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      const errorBody = await response.text();
      throw new AppError({
        message: `API error fetching deathmatch details: ${response.status} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { endpoint: apiUrl, responseBody: errorBody }
      });
    }

    const data = await response.json();

    // Assuming API returns { details: DeathmatchDetails[] }
    if (!data || !Array.isArray(data.details)) {
      throw new AppError({
        message: 'Invalid response structure from all deathmatch details API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const details: DeathmatchDetails[] = data.details;
    
    console.log(`Caching ${details.length} deathmatch details (completed=${includeCompleted}, limit=${limit})`);
    cacheManager.set(cacheKey, details, CACHE_TTL.SHORT);
    
    return details;

  } catch (error) {
    const appError = handleError(error, 'getAllDeathmatchDetails', { includeCompleted, limit });
    logError(appError, 'getAllDeathmatchDetails');
    
    // Treat potential 404 as empty list
    if (appError.type === ErrorType.NOT_FOUND || (appError.type === ErrorType.NETWORK && appError.statusCode === 404)) {
       console.warn('All deathmatch details endpoint returned 404, returning empty array.');
      return [];
    }
    
    throw appError;
  }
}

/**
 * Updates a deathmatch with the given ID via the API.
 * 
 * @param id - The ID of the deathmatch to update
 * @param deathmatchData - The deathmatch data to update
 * @returns The updated deathmatch object from the API
 * @throws AppError if the operation fails
 */
export async function updateDeathmatch(id: string, deathmatchData: UpdateDeathmatch): Promise<Deathmatch> {
  try {
    // Validate IDs and incoming data structure
    validateUUID(id, 'id');
    validate(deathmatchData, deathmatchSchema, 'deathmatch'); // Use Zod schema

    console.log(`Updating deathmatch ${id} via API...`);
    const apiUrl = `/api/deathmatches/${encodeURIComponent(id)}`;
    const response = await fetchWithAuth(apiUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(deathmatchData), // Send the update payload
    });

    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
       if (statusCode === 404) {
         throw new AppError({
           message: `Deathmatch with ID ${id} not found via API for update`,
           type: ErrorType.NOT_FOUND,
           statusCode: 404,
           metadata: { endpoint: apiUrl, deathmatchId: id }
         });
       }
      throw new AppError({
        message: `API error updating deathmatch ${id}: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, deathmatchId: id }
      });
    }

    const data = await response.json();

    // Assuming API returns { deathmatch: Deathmatch }
    if (!data || typeof data.deathmatch !== 'object' || data.deathmatch === null) {
       throw new AppError({
         message: `Invalid response structure from update deathmatch API for ${id}`,
         type: ErrorType.UNEXPECTED,
         statusCode: 500,
         metadata: { responseData: data }
       });
    }
    
    const updatedDeathmatch: Deathmatch = data.deathmatch;

    // Invalidate relevant caches
    console.log(`Invalidating cache for updated deathmatch ${id}`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, id, deathmatchData);
    
    return updatedDeathmatch;

  } catch (error) {
    const appError = handleError(error, 'updateDeathmatch', { id, deathmatchData });
    logError(appError, 'updateDeathmatch');
    throw appError;
  }
}

/**
 * Records bracket positions for a deathmatch via the backend API.
 * @param deathmatchId - The deathmatch's UUID
 * @param bracketPositions - Array of bracket positions with poet and poem info
 * @returns Array of created bracket position records from the API response
 * @throws AppError if the operation fails
 */
export async function recordBracketPositions(
  deathmatchId: string,
  bracketPositions: Array<{ 
    position: number; 
    poet_id: string; 
    poem_id: string;
  }>
): Promise<DeathmatchBracket[]> {
  try {
    // Validate input
    validateUUID(deathmatchId, 'deathmatchId');
    if (!bracketPositions || bracketPositions.length === 0) {
      throw new AppError('Cannot record empty bracket positions', ErrorType.VALIDATION);
    }
    // Further validation of content within bracketPositions could be added here if needed
    
    console.log(`Recording ${bracketPositions.length} bracket positions for deathmatch ${deathmatchId} via API...`);
    const apiUrl = `/api/deathmatches/${encodeURIComponent(deathmatchId)}/brackets`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send the raw positions array, API will handle record creation
      body: JSON.stringify({ bracketPositions }), 
    });

    if (!response.ok) {
      const errorBody = await response.text();
      const statusCode = response.status;
      throw new AppError({
        message: `API error recording bracket positions: ${statusCode} ${response.statusText}`,
        type: ErrorType.NETWORK,
        statusCode: statusCode,
        metadata: { endpoint: apiUrl, responseBody: errorBody, deathmatchId }
      });
    }
    
    const data = await response.json();

    // Assuming API returns { brackets: DeathmatchBracket[] }
    if (!data || !Array.isArray(data.brackets)) {
      throw new AppError({
        message: 'Invalid response structure from record bracket positions API',
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { responseData: data }
      });
    }

    const createdBrackets: DeathmatchBracket[] = data.brackets;
    
    // Invalidate relevant caches
    console.log(`Invalidating cache after recording brackets for ${deathmatchId}`);
    invalidateRelatedCaches(CacheEntityType.DEATHMATCH, deathmatchId);
    
    return createdBrackets;

  } catch (error) {
    const appError = handleError(error, 'recordBracketPositions', { deathmatchId, bracketPositionsCount: bracketPositions?.length });
    logError(appError, 'recordBracketPositions');
    throw appError;
  }
}

/**
 * Determine the round type based on round number and bracket size
 * @param roundNumber - The round number (1-based)
 * @param bracketSize - The size of the bracket (default: 8)
 * @returns The round type ('selection', 'semifinal', or 'final')
 */
export function determineRoundType(roundNumber: number, bracketSize: number = 8): string {
  // For 8-poet bracket:
  // Rounds 1-4: Selection (first round)
  // Rounds 5-6: Semifinal (second round)
  // Round 7: Final (third round)
  const totalRounds = Math.log2(bracketSize);
  const remainingRounds = totalRounds - Math.floor((roundNumber - 1) / (bracketSize / 2));
  
  if (remainingRounds <= 1) {
    return 'final';
  } else if (remainingRounds === 2) {
    return 'semifinal';
  } else {
    return 'selection';
  }
} 